import React, { useEffect, useState } from "react";
import { ArrowDownToLine, Loader2, Wallet, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { formatBRL, formatDateTime, parseCurrencyInput } from "@/lib/format";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export default function Withdraw() {
  const [balance, setBalance] = useState(0);
  const [pixKey, setPixKey] = useState("");
  const [pixKeyType, setPixKeyType] = useState("CPF");
  const [amount, setAmount] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [stats, list] = await Promise.all([
        api.get("/stats/dashboard"),
        api.get("/withdrawals"),
      ]);
      setBalance(stats.data.balance_cents);
      setHistory(list.data);
    } catch {
      toast.error("Erro ao carregar dados de saque");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    const reais = parseCurrencyInput(amount);
    if (reais <= 0) {
      toast.error("Informe um valor válido");
      return;
    }
    if (!pixKey.trim()) {
      toast.error("Informe a chave PIX de destino");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/withdrawals", {
        pix_key: pixKey.trim(),
        pix_key_type: pixKeyType,
        amount_reais: reais,
      });
      toast.success("Saque solicitado com sucesso");
      setAmount("");
      setPixKey("");
      await loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao solicitar saque");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="withdraw-page">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1">
          Conta Woovi
        </p>
        <h1 className="font-display text-3xl md:text-4xl font-bold">Saque PIX</h1>
        <p className="text-sm text-muted-foreground mt-2">
          Transfira o saldo disponível para sua chave PIX em segundos.
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="bg-primary/5 border-primary/30 pix-glow lg:col-span-1" data-testid="balance-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-3">
              <Wallet className="w-4 h-4 text-primary" />
              <p className="text-xs uppercase tracking-wider text-muted-foreground">
                Saldo disponível
              </p>
            </div>
            <p className="font-display font-bold text-4xl font-mono-num text-primary">
              {loading ? <Loader2 className="w-8 h-8 animate-spin" /> : formatBRL(balance)}
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              Valor calculado a partir das suas vendas recebidas menos saques já realizados.
            </p>
          </CardContent>
        </Card>

        <Card className="bg-card border-border lg:col-span-2" data-testid="withdraw-form-card">
          <CardHeader>
            <CardTitle className="font-display flex items-center gap-2">
              <ArrowDownToLine className="w-4 h-4 text-primary" />
              Solicitar saque
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-4">
              <div className="grid sm:grid-cols-3 gap-3">
                <div className="space-y-2 sm:col-span-1">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                    Tipo
                  </Label>
                  <Select value={pixKeyType} onValueChange={setPixKeyType}>
                    <SelectTrigger className="bg-secondary/40" data-testid="pixkey-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="CPF">CPF</SelectItem>
                      <SelectItem value="CNPJ">CNPJ</SelectItem>
                      <SelectItem value="EMAIL">E-mail</SelectItem>
                      <SelectItem value="PHONE">Telefone</SelectItem>
                      <SelectItem value="RANDOM">Chave aleatória</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2 sm:col-span-2">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                    Chave PIX destino
                  </Label>
                  <Input
                    data-testid="pixkey-input"
                    value={pixKey}
                    onChange={(e) => setPixKey(e.target.value)}
                    placeholder="Ex: 000.000.000-00"
                    className="bg-secondary/40"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                  Valor do saque (R$)
                </Label>
                <Input
                  data-testid="withdraw-amount"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0,00"
                  inputMode="decimal"
                  className="h-12 text-xl font-display font-bold bg-secondary/40"
                />
                <button
                  type="button"
                  onClick={() => setAmount((balance / 100).toString().replace(".", ","))}
                  className="text-xs text-primary hover:underline"
                  data-testid="withdraw-max"
                >
                  Sacar saldo total ({formatBRL(balance)})
                </button>
              </div>
              <Button
                type="submit"
                disabled={submitting || balance <= 0}
                data-testid="withdraw-submit"
                className="w-full h-12 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <ArrowDownToLine className="w-4 h-4 mr-2" />
                    Solicitar saque PIX
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-card border-border" data-testid="withdraw-history-card">
        <CardHeader>
          <CardTitle className="font-display text-lg">Histórico de saques</CardTitle>
        </CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground">
              Nenhum saque realizado até o momento.
            </div>
          ) : (
            <div className="rounded-lg border border-border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-secondary/30 border-border">
                    <TableHead className="text-xs uppercase">Data</TableHead>
                    <TableHead className="text-xs uppercase">Chave PIX</TableHead>
                    <TableHead className="text-xs uppercase">Tipo</TableHead>
                    <TableHead className="text-xs uppercase">Valor</TableHead>
                    <TableHead className="text-xs uppercase">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.map((w) => (
                    <TableRow key={w.id} className="border-border" data-testid={`wd-row-${w.id}`}>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDateTime(w.completed_at || w.created_at)}
                      </TableCell>
                      <TableCell className="font-mono-num text-sm">{w.pix_key}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{w.pix_key_type}</TableCell>
                      <TableCell className="font-mono-num font-semibold">
                        {formatBRL(w.amount)}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="border-primary/40 text-primary bg-primary/10">
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          {w.status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
